import { inject, Injectable } from '@angular/core';
import { ApplicationActions } from '@common/store/navigation/actions/application.actions';
import { ViewStateActions, ViewStateApiActions } from '@common/store/navigation/actions/viewstate.actions';
import { selectCurrentApplication } from '@common/store/navigation/selectors/navigation.selectors';
import { selectCurrentViewState } from '@common/store/navigation/selectors/viewstate.selectors';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { environment } from '@src/environments/environment';
import { MultipleFeedViewStateRecord } from '@src/suite/common/data-models/records/mutiple-feed-viewstate-record';
import { PublicationViewStateRecord } from '@src/suite/common/data-models/records/publication-viewstate-record';
import { SearchViewStateRecord } from '@src/suite/common/data-models/records/search-viewstate-record';
import { SingleViewStateRecord } from '@src/suite/common/data-models/records/single-viewstate-record';
import { TopicViewStateRecord } from '@src/suite/common/data-models/records/topic-viewstate-record';
import { ViewStateTypesEnum } from '@src/suite/common/enums/viewstate-types';
import { CustomCardFieldsService } from '@src/suite/common/services/custom-card-fields-service';
import { LayoutState } from '@src/suite/common/services/layout-state';
import { PublicationChannelsService } from '@src/suite/common/services/publication-channels-service';
import { CastActions, CastApiActions } from '@src/suite/common/store/cast/cast.actions';
import { selectCreateViewStateViewState } from '@src/suite/common/store/createviewstate/createviewstate.selectors';
import { selectGroupSort, selectPSTGroupPrivileges } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { ListActions } from '@src/suite/common/store/list/list.actions';
import { NavigationApiActions } from '@src/suite/common/store/navigation/actions/navigation.actions';
import { PlaybookApiActions } from '@src/suite/common/store/playbook/playbook.actions';
import { PublicationRecordActions, RecordHttpActions } from '@src/suite/common/store/records/records.actions';
import { getGroupByOptions, getLayoutOptions, getSortByOptions } from '@src/suite/common/utils/viewstate-utils';
import { WireActions, WireApiActions } from '@src/suite/discover/store/actions/discover.actions';
import { catchError, from, map, mergeMap, of, switchMap } from 'rxjs';

@Injectable()
export class ViewStateEffects {
    private actions$ = inject(Actions);
    private store = inject(Store);
    private viewStateService = inject(LayoutState);
    private publicationChannelsService = inject(PublicationChannelsService);
    private customCardFieldsService = inject(CustomCardFieldsService);

    setCurrentViewState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ViewStateActions.setCurrentViewState),
            concatLatestFrom(() => [this.store.select(selectI18N), this.store.select(selectPSTGroupPrivileges), this.store.select(selectGroupSort)]),
            switchMap(([action, i18n, PSTGroupPrivs, groupSort]) => {
                const vs = action.vs;
                if (!vs) return of(ViewStateActions.setCurrentViewStateSuccess({ vs: null, layout: null, sortBy: null }));
                const layout = getLayoutOptions(vs.viewStateType, i18n, PSTGroupPrivs);
                const sortBy = getSortByOptions(vs.viewStateType, groupSort, i18n);
                const groupBy = getGroupByOptions(vs.viewStateType, groupSort, i18n);
                if (vs instanceof SingleViewStateRecord || vs instanceof PublicationViewStateRecord) {
                    if (vs instanceof TopicViewStateRecord || vs instanceof SearchViewStateRecord || vs instanceof PublicationViewStateRecord) {
                        // Set default custom card fields
                        if (!vs.viewStateCustomCardItems || !Array.isArray(vs.viewStateCustomCardItems)) {
                            vs.viewStateCustomCardItems = Object.assign([], this.customCardFieldsService.CustomCardFields);
                            (<SingleViewStateRecord>vs).isViewStateCustomCardItemsBlank = true;
                        }
                    }
                }
                if (vs instanceof MultipleFeedViewStateRecord) {
                    if (typeof vs.viewStateSequence === 'string') {
                        const sequence = JSON.parse(vs.viewStateSequence);
                        return from(this.viewStateService.getViewStateRecordByGuids(sequence)).pipe(
                            mergeMap(res => {
                                if (res.status) {
                                    vs.viewStates = res.data;
                                    return of(ViewStateActions.setCurrentViewStateSuccess({ vs: vs, layout, sortBy, groupBy }));
                                }
                                return of(ViewStateActions.setCurrentViewStateError({ error: res.message }));
                            })
                        );
                    }
                }
                if (vs instanceof PublicationViewStateRecord) {
                    return from(this.publicationChannelsService.getPublicationChannelRecord(vs.publicationChannelGUID)).pipe(
                        mergeMap(res => {
                            if (res.status) {
                                vs.publicationChannel = res.data;
                                return [
                                    ViewStateActions.setCurrentViewStateSuccess({ vs: vs, layout }),
                                    // CastApiActions.loadPublicationLists({ vs }),
                                    PublicationRecordActions.updatePublicationChannels({ records: [res.data] }),
                                ];
                            }
                            return of(ViewStateActions.setCurrentViewStateError({ error: res.message }));
                        })
                    );
                }
                return of(ViewStateActions.setCurrentViewStateSuccess({ vs, layout, sortBy, groupBy }));
            })
        );
    });

    setCurrentViewStateSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ViewStateActions.setCurrentViewStateSuccess),
            concatLatestFrom(() => [this.store.select(selectCurrentApplication), this.store.select(selectCurrentViewState)]),
            switchMap(([_, application, vs]) => {
                if (!vs) {
                    return of(WireApiActions.stopPolling({ restart: false, vs: null, interval: null }));
                } else {
                    if (application === 'discover') {
                        const actions = [];
                        if (vs.viewStateType === ViewStateTypesEnum.WIRESEARCH) {
                            actions.push(WireActions.loading({ isLoading: true }));
                            actions.push(WireActions.searchWires({ vs }));
                        } else {
                            actions.push(
                                WireApiActions.startPolling({
                                    interval: this.getPollInvervalInSeconds(),
                                })
                            );
                        }
                        return actions;
                    }
                    if (application === 'live') {
                        return [ListActions.setLoading({ isLoading: true }), ListActions.clearNewListGuids(), RecordHttpActions.loadRundowns({ vs })];
                    }
                    if (application === 'playbook') {
                        if (vs.viewStateType !== ViewStateTypesEnum.SINGLEITEM) {
                            if (vs.viewStateType === ViewStateTypesEnum.SEARCH) {
                                return [
                                    ListActions.setLoading({ isLoading: true }),
                                    ListActions.clearNewListGuids(),
                                    RecordHttpActions.loadPlaybookObjects({ vs }),
                                    RecordHttpActions.loadRundownObjects({ vs }),
                                ];
                            } else {
                                return [ListActions.setLoading({ isLoading: true }), ListActions.clearNewListGuids(), RecordHttpActions.loadPlaybookObjects({ vs })];
                            }
                        }
                    }
                    if (application === 'cast' && vs && vs instanceof PublicationViewStateRecord) {
                        const layout = getLayoutOptions(vs.viewStateType, this.store.selectSignal(selectI18N)(), this.store.selectSignal(selectPSTGroupPrivileges)());
                        return [
                            CastApiActions.loadPublicationList({ vs, setMaxPublicationRevisionNumber: true }),
                            CastApiActions.loadViewStates(),
                            PlaybookApiActions.loadTopicTemplates(),
                            CastActions.setLoadingList({ listLoading: true }),
                            ViewStateActions.updateViewStateLayout({ layout: vs.viewStateLayout.Value || layout.Default }),
                            vs.viewStateGUID ? CastActions.setCurrentViewState({ guid: vs.viewStateGUID }) : null,
                        ].filter(x => x);
                    }
                    return of(ApplicationActions.noOpAction());
                }
            })
        );
    });

    crudViewState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationApiActions.updateNavigationItemSuccess),
            concatLatestFrom(() => [this.store.select(selectCurrentViewState), this.store.select(selectCreateViewStateViewState)]),
            map(([_, vs, updated]) => {
                if (vs?.guid === updated?.guid) {
                    return ViewStateActions.viewStateChanged();
                }
                return ApplicationActions.noOpAction();
            })
        );
    });

    updateViewState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                ViewStateActions.viewStateChanged,
                ViewStateActions.addViewStateFilters,
                ViewStateActions.removeViewStateFilter,
                ViewStateActions.updateViewStateFilter,
                ViewStateActions.adjustDateRange
            ),
            concatLatestFrom(() => [this.store.select(selectCurrentApplication), this.store.select(selectCurrentViewState)]),
            mergeMap(([_, application, vs]) => {
                if (application === 'discover') {
                    return of(
                        WireApiActions.stopPolling({
                            restart: true,
                            vs: vs,
                            interval: this.getPollInvervalInSeconds(),
                        })
                    );
                }
                if (application === 'live') {
                    return [ListActions.setLoading({ isLoading: true }), ListActions.clearNewListGuids(), RecordHttpActions.loadRundowns({ vs })];
                }
                if (application === 'playbook') {
                    if (vs.viewStateType !== ViewStateTypesEnum.SINGLEITEM) {
                        if (vs.viewStateType === ViewStateTypesEnum.SEARCH) {
                            return [
                                ListActions.setLoading({ isLoading: true }),
                                ListActions.clearNewListGuids(),
                                RecordHttpActions.loadPlaybookObjects({ vs }),
                                RecordHttpActions.loadRundownObjects({ vs }),
                            ];
                        } else {
                            return [ListActions.setLoading({ isLoading: true }), ListActions.clearNewListGuids(), RecordHttpActions.loadPlaybookObjects({ vs })];
                        }
                    }
                }
                if (application === 'cast' && vs && vs instanceof PublicationViewStateRecord) {
                    // return of(CastApiActions.loadPublicationList({ vs }));
                    return [CastApiActions.loadPublicationList({ vs, setMaxPublicationRevisionNumber: true }), CastActions.setLoadingList({ listLoading: true })];
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    applyTeamCustomCardFields$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ViewStateApiActions.applyTeamCustomCardFields),
            concatLatestFrom(() => this.store.select(selectCurrentViewState)),
            mergeMap(([action, vs]) => {
                if (vs instanceof TopicViewStateRecord || vs instanceof SearchViewStateRecord || vs instanceof PublicationViewStateRecord) {
                    return from(this.viewStateService.updateTeamCustomCardFields(action.fields, action.team)).pipe(
                        map(() => ViewStateApiActions.applyTeamCustomCardFieldsSuccess({ fields: this.customCardFieldsService.CustomCardFields })),
                        catchError(error => of(ViewStateApiActions.applyTeamCustomCardFieldsError({ error: error })))
                    );
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    loadViewStateRecords$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ViewStateApiActions.loadViewStateRecords),
            concatLatestFrom(() => this.store.select(selectCurrentApplication)),
            mergeMap(([action, app]) => {
                const application = action.application || app;
                return from(this.viewStateService.loadViewStateRecords(action.group, action.creators, application)).pipe(
                    map(() => ViewStateApiActions.loadViewStateRecordsSuccess()),
                    catchError(error => of(ViewStateApiActions.loadViewStateRecordsError({ error })))
                );
            })
        );
    });

    deleteViewStateRecord$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ViewStateApiActions.deleteViewStateRecord),
            concatLatestFrom(() => this.store.select(selectCurrentApplication)),
            mergeMap(([action, application]) => {
                if (application === 'discover') {
                    return this.viewStateService.deleteViewStateDiscoverRecord(action.vs).pipe(
                        map(res => ViewStateApiActions.deleteViewStateRecordSuccess({ status: res.status })),
                        catchError(error => of(ViewStateApiActions.deleteViewStateRecordError({ error: error })))
                    );
                } else {
                    return this.viewStateService.deleteViewStateRecord(action.vs).pipe(
                        map(res => ViewStateApiActions.deleteViewStateRecordSuccess({ status: res.status })),
                        catchError(error => of(ViewStateApiActions.deleteViewStateRecordError({ error: error })))
                    );
                }
            })
        );
    });

    deleteViewStateRecordSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ViewStateApiActions.deleteViewStateRecordSuccess),
            map(() => {
                return ViewStateApiActions.loadViewStateRecords({ group: 'all' });
            })
        );
    });

    resetNodeExpandedState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                ViewStateActions.updateViewStateLayout,
                ViewStateActions.updateViewStateGroupBy,
                ViewStateActions.updateViewStateSortBy,
                ViewStateActions.updateViewStateSortBySortOrder,
                ViewStateActions.updateViewStateGroupBySortOrder,
                ViewStateActions.addViewStateFilters,
                ViewStateActions.removeViewStateFilter,
                ViewStateActions.updateViewStateFilter,
                ViewStateActions.setCurrentViewState,
                ViewStateActions.updateViewStateViewStateGuid
            ),
            map(() => {
                return ListActions.resetNodeExpandedState();
            })
        );
    });

    constructor() {}

    private _pollInterval: number;
    public getPollInvervalInSeconds() {
        if (this._pollInterval) {
            return this._pollInterval;
        }

        this._pollInterval = environment.solr_poll_interval_seconds ? 10000 : environment.solr_poll_interval_seconds * 1000;
        return this._pollInterval;
    }
}
